<template>
  <header
    :class="{
      fixed,
      visible,
      'no-transition': noTransition,
      'menu-active': menuActive
    }"
    class="navigation-bar"
  >
    <nav id="nav" class="nav grid">
      <ul class="nav-elements">
        <li>
          <Logo />
        </li>
        <ClientOnly>
          <NavigationDesktopLinks
            v-if="isDesktop && menu?.items?.length"
            :items="menu?.items"
          />
        </ClientOnly>

        <li v-if="menu?.items?.length" class="s-lg-down">
          <NavigationHamburger
            :active="menuActive"
            class="s-lg-down"
            @click="openMenu"
          />
        </li>
      </ul>
    </nav>
  </header>
</template>

<script setup lang="ts">
import type { PublicMainMenu } from '@/service/__generated-api'

withDefaults(
  defineProps<{ menu: Partial<PublicMainMenu> | null; menuActive: boolean }>(),
  {
    menuActive: false
  }
)

const emit = defineEmits<{ (event: 'open'): void }>()
const fixed = ref(false)
const visible = ref(true)
const noTransition = ref(false)
const isDesktop = useIsDesktop()

const openMenu = () => emit('open')

onMounted(() => {
  let { scrollY } = window
  window.addEventListener('scroll', () => {
    const currentScrollY = window.scrollY
    const navHeight = document.querySelector('#nav')?.clientHeight || 116
    if (currentScrollY < navHeight) {
      // hits start of a page
      visible.value = true
      noTransition.value = true
      fixed.value = false
      document.body.classList.remove('hidden-header')
    } else if (currentScrollY < scrollY) {
      // scrolling up
      visible.value = true
      noTransition.value = false
      fixed.value = true
      document.body.classList.remove('hidden-header')
    } else {
      // scrolling down
      visible.value = false
      noTransition.value = !fixed.value
      fixed.value = true
      document.body.classList.add('hidden-header')
    }
    scrollY = currentScrollY
  })
})
</script>

<style lang="scss" scoped>
.navigation-bar {
  min-height: $nav-height;
}

.nav {
  position: absolute;
  inset: 0 0 auto;
  z-index: 1;
  min-height: inherit;
  padding-block: rem(20px) rem(18px);
  pointer-events: none;

  opacity: 0;
  transition: opacity 0.3s;

  &::after {
    @include fill-space;
    z-index: -1;
    content: '';
    background-color: $layout-background-01;
  }

  .fixed & {
    position: fixed;
  }

  .no-transition & {
    transition: none;
  }

  .visible &,
  .menu-active &,
  .navigation-bar:focus-within & {
    pointer-events: auto;
    opacity: 1;
  }
}

.nav-elements {
  @include column(xs, 12);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
